import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { Conversation, EagleOverview, Summary, VeeamVmEntry } from '../../api/types'
import { usePolling } from '../../hooks/useAsync'
import { localeCorrente, maiuscola, t } from '../../i18n/lingua'
import { CategoryBars, Donut, StatusStrip, TrafficArea, formatBytes, type StatusSlice } from '../charts'
import { Causa } from '../CausaRadice'
import { FinestraParametro, type ParametroDaAprire } from '../FinestraParametro'
import { timeAgo } from '../common'
import { DataTable } from '../DataTable'
import { GraficoStati } from '../GraficoStati'
import { etichettaStato, tonoStato } from '../EagleStato'
import { etichettaParametro, formatConUnita } from '../eagleTesti'
import { ScanStats } from '../ScanStats'
import { TitoloWidgetContext, Widget, type MisuraWidget } from '../Griglia'
import { CATALOGO_EAGLE } from './catalogoEagle'
import { Immagine, Testo } from './generali'
import { Segmenti, segmentiDaConteggi } from './graficiEagle'
import { ElencoBarre, NumeroGrande, type RigaBarra } from './primitivi'

/**
 * Il catalogo dei widget della dashboard personalizzabile.
 *
 * Ogni widget è una domanda con una risposta piccola — un numero, una
 * classifica, una ruota, un andamento — alimentata da un'API che la console
 * ha già. Si dichiara con gruppo, misura predefinita, modulo che serve per
 * vederlo e, se ha regolazioni (la finestra del traffico, i giorni), le sue
 * opzioni. Il disegno è dello stesso componente: il riquadro sa il suo titolo.
 *
 * Ogni widget ricarica da sé (60 s; il traffico e Eagle ogni minuto pieno) e
 * mentre ricarica tiene il disegno precedente: su un monitor a parete niente
 * deve lampeggiare.
 */

export type GruppoWidget = 'panoramica' | 'traffico' | 'sicurezza' | 'protezione' | 'capacita' | 'eagle' | 'generale'

export interface OpzioneWidget {
  key: string
  label: () => string
  valori: { value: string; label: () => string }[]
  predefinito: string
  /** Un'opzione con un editor suo: la scelta «CI e parametro», l'elenco di serie, i due capi di un link. */
  dinamica?: 'ci_param' | 'serie' | 'link' | 'vista_topologia' | 'bersaglio' | 'immagine'
  /** Come si modifica: un menu (predefinito), una spunta, un testo libero, un testo su più righe. */
  tipo?: 'select' | 'bool' | 'testo' | 'multilinea'
  /** Una riga di aiuto sotto il campo, nell'editor. */
  aiuto?: () => string
  /** Per l'elenco di serie: solo parametri di questi tipi, e quante al massimo. */
  soloTipi?: string[]
  massimo?: number
}

export interface DefinizioneWidget {
  tipo: string
  gruppo: GruppoWidget
  label: () => string
  descrizione: () => string
  /** Il modulo della console che serve per vederlo (usePermessiModuli). */
  modulo?: string
  /** Solo con Eagle. */
  eagle?: boolean
  size: MisuraWidget
  tall?: boolean
  opzioni?: OpzioneWidget[]
  Componente: (p: { options: Record<string, unknown>; sinottico: boolean }) => ReactNode
}

export const GRUPPI_WIDGET: { key: GruppoWidget; label: () => string }[] = [
  { key: 'panoramica', label: () => t('Panoramica') },
  { key: 'traffico', label: () => t('Traffico') },
  { key: 'sicurezza', label: () => t('Sicurezza') },
  { key: 'protezione', label: () => t('Protezione') },
  { key: 'capacita', label: () => t('Capacità e costi') },
  { key: 'eagle', label: () => 'Eagle' },
  { key: 'generale', label: () => t('Composizione') },
]

const FINESTRA: OpzioneWidget = {
  key: 'window', predefinito: '24h', label: () => t('Finestra'),
  valori: [
    { value: '1h', label: () => t('1 ora') }, { value: '6h', label: () => t('6 ore') },
    { value: '24h', label: () => t('24 ore') }, { value: '7d', label: () => t('7 giorni') },
  ],
}
const GIORNI: OpzioneWidget = {
  key: 'days', predefinito: '30', label: () => t('Giorni'),
  valori: [{ value: '7', label: () => '7' }, { value: '30', label: () => '30' }, { value: '90', label: () => '90' }],
}
const ORE_EAGLE: OpzioneWidget = {
  key: 'hours', predefinito: '24', label: () => t('Finestra'),
  valori: [{ value: '6', label: () => t('6 ore') }, { value: '24', label: () => t('24 ore') }, { value: '168', label: () => t('7 giorni') }],
}

const ogniMinuto = 60_000
const finestraDi = (o: Record<string, unknown>) => String(o.window ?? '24h')
const giorniDi = (o: Record<string, unknown>) => Number(o.days ?? 30)

// ---------------------------------------------------------------- panoramica

function useSummary() {
  return usePolling(() => api.summary(), ogniMinuto)
}

function CIGestiti() {
  const s = useSummary()
  const d: Summary | null = s.data
  return (
    <Widget titolo={t('CI gestiti')} caricando={s.loading} errore={s.error} link={<Link to="/cis">{t('apri')}</Link>}>
      {d && <NumeroGrande valore={d.cis.managed} etichetta={t('riconosciuti da almeno una fonte')}
                          sotto={t('{n} in tutto', { n: d.cis.total.toLocaleString(localeCorrente()) })} />}
    </Widget>
  )
}

function HostNonGestiti() {
  const s = useSummary()
  const d = s.data
  return (
    <Widget titolo={t('Host non gestiti')} caricando={s.loading} errore={s.error} link={<Link to="/cis?type=unmanaged_host">{t('apri')}</Link>}>
      {d && <NumeroGrande valore={d.cis.unmanaged} tono={d.cis.unmanaged > 0 ? 'warn' : 'ok'}
                          etichetta={t('visti in rete senza accesso')} />}
    </Widget>
  )
}

function SondeOnline() {
  const s = useSummary()
  const d = s.data
  return (
    <Widget titolo={t('Sonde')} caricando={s.loading} errore={s.error} link={<Link to="/fleet">{t('apri')}</Link>}>
      {d && <NumeroGrande valore={`${d.probes.online}/${d.probes.total}`}
                          tono={d.probes.total === 0 ? '' : d.probes.online === d.probes.total ? 'ok' : d.probes.online === 0 ? 'error' : 'warn'}
                          etichetta={t('in linea su totali')} />}
    </Widget>
  )
}

function JobFalliti() {
  const s = useSummary()
  const d = s.data
  return (
    <Widget titolo={t('Job falliti')} caricando={s.loading} errore={s.error} link={<Link to="/jobs">{t('apri')}</Link>}>
      {d && <NumeroGrande valore={d.jobs.failed} tono={d.jobs.failed > 0 ? 'error' : 'ok'}
                          etichetta={t('su {n} job', { n: d.jobs.total.toLocaleString(localeCorrente()) })} />}
    </Widget>
  )
}

function CIPerTipo() {
  const s = useSummary()
  const d = s.data
  return (
    <Widget titolo={t('CI per tipo')} caricando={s.loading} errore={s.error} link={<Link to="/cis">{t('apri')}</Link>}>
      {d && (Object.keys(d.cis.by_type).length === 0
        ? <p className="muted" style={{ margin: 0 }}>{t('Nessun CI. Avvia una discovery per popolare la CMDB.')}</p>
        : <CategoryBars data={Object.entries(d.cis.by_type).map(([k, v]) => ({ key: k, label: k, value: v }))} />)}
    </Widget>
  )
}

type Tono = [string, () => string, StatusSlice['tone']]
const JOB_TONES: Tono[] = [
  ['succeeded', () => t('Riusciti'), 'ok'], ['running', () => t('In corso'), 'info'],
  ['pending', () => t('In attesa'), 'info'], ['failed', () => t('Falliti'), 'error'],
]
const PROBE_TONES: Tono[] = [
  ['online', () => t('In linea'), 'ok'], ['degraded', () => t('Degradate'), 'warn'], ['offline', () => t('Non raggiungibili'), 'error'],
]
function fette(counts: Record<string, number>, tones: Tono[]): StatusSlice[] {
  const note = new Set(tones.map(([k]) => k))
  const out: StatusSlice[] = tones.filter(([k]) => (counts[k] ?? 0) > 0).map(([k, l, tone]) => ({ key: k, label: l(), value: counts[k], tone }))
  // Gli stati non previsti si mostrano: nasconderli racconterebbe una situazione più semplice della vera.
  return [...out, ...Object.entries(counts).filter(([k, v]) => !note.has(k) && v > 0).map(([k, v]) => ({ key: k, label: k, value: v, tone: 'info' as const }))]
}

function JobStati() {
  const s = useSummary()
  const d = s.data
  return (
    <Widget titolo={t('Job per stato')} caricando={s.loading} errore={s.error} link={<Link to="/jobs">{t('registro')}</Link>}>
      {d && (d.jobs.total === 0 ? <p className="muted" style={{ margin: 0 }}>{t('Nessun job eseguito.')}</p>
        : <StatusStrip data={fette(d.jobs.by_status, JOB_TONES)} />)}
    </Widget>
  )
}

function SondeStati() {
  const s = useSummary()
  const d = s.data
  return (
    <Widget titolo={t('Sonde per stato')} caricando={s.loading} errore={s.error} link={<Link to="/fleet">{t('apri')}</Link>}>
      {d && <StatusStrip data={fette(d.probes.by_status, PROBE_TONES)} emptyHint={t('Nessuna sonda arruolata.')} />}
    </Widget>
  )
}

function Scansioni({ options }: { options: Record<string, unknown> }) {
  return (
    <Widget titolo={t('Scansioni')} sotto={t('ultimi {n} giorni', { n: giorniDi(options) })}>
      <ScanStats compact days={giorniDi(options)} />
    </Widget>
  )
}

function Movimenti() {
  const s = usePolling(() => api.ciOverview(), ogniMinuto)
  const d = s.data
  return (
    <Widget titolo={t('Movimenti dell’inventario')} sotto={t('ultimi 7 giorni')} caricando={s.loading} errore={s.error} link={<Link to="/cis">{t('apri')}</Link>}>
      {d && (
        <div className="eagle-testata" style={{ gridTemplateColumns: 'repeat(3, 1fr)', margin: 0, gap: 8 }}>
          <NumeroGrande valore={d.nuovi_7g} etichetta={t('nuovi')} tono={d.nuovi_7g > 0 ? 'ok' : ''} />
          <NumeroGrande valore={d.non_visti_7g} etichetta={t('non più visti')} tono={d.non_visti_7g > 0 ? 'warn' : ''} />
          <NumeroGrande valore={d.non_classificati} etichetta={t('da classificare')} tono={d.non_classificati > 0 ? 'warn' : ''} />
        </div>
      )}
    </Widget>
  )
}

const TONI_SEVERITA: Record<string, StatusSlice['tone']> = { critical: 'error', high: 'error', medium: 'warn', low: 'info', info: 'info' }

function Reperti() {
  const s = usePolling(() => api.ciOverview(), ogniMinuto)
  const d = s.data
  return (
    <Widget titolo={t('Rischi sui CI')} caricando={s.loading} errore={s.error} link={<Link to="/security-health">{t('apri')}</Link>}>
      {d && (d.reperti.ci === 0 ? <p className="muted" style={{ margin: 0 }}>{t('Nessun rischio.')}</p> : (
        <>
          <StatusStrip data={Object.entries(d.reperti.per_severita).filter(([, v]) => v > 0)
            .map(([k, v]) => ({ key: k, label: k, value: v, tone: TONI_SEVERITA[k] ?? 'info' }))} />
          <p className="muted eagle-piccolo" style={{ margin: '6px 0 0' }}>{t('su {n} CI', { n: d.reperti.ci })}</p>
        </>
      ))}
    </Widget>
  )
}

// ------------------------------------------------------------------ traffico

function TrafficoVolume({ options }: { options: Record<string, unknown> }) {
  const w = finestraDi(options)
  const s = usePolling(() => api.flowSeries({ window: w }), ogniMinuto, [w])
  const d = s.data
  return (
    <Widget titolo={t('Volume di traffico')} sotto={d ? t('{v} nella finestra', { v: formatBytes(d.bytes_total) }) : undefined}
            caricando={s.loading} errore={s.error} link={<Link to="/flows">{t('apri')}</Link>}>
      {d && <TrafficArea points={d.points} height={150} stepSeconds={d.step_seconds} />}
    </Widget>
  )
}

/** Chi fa più traffico: la somma di ciò che ogni indirizzo manda e riceve. */
function TopTalkers({ options, sinottico }: { options: Record<string, unknown>; sinottico: boolean }) {
  const w = finestraDi(options)
  const s = usePolling(() => api.conversations({ window: w, limit: 300 }), ogniMinuto, [w])
  const per = new Map<string, { nome: string; ci: string | null; classe: string | null; bytes: number }>()
  for (const c of s.data ?? []) {
    for (const lato of [['src', c.src_addr, c.src_label, c.src_ci_id, c.src_class], ['dst', c.dst_addr, c.dst_label, c.dst_ci_id, c.dst_class]] as const) {
      const [, addr, label, ci, classe] = lato
      const k = ci ?? addr
      const v = per.get(k) ?? { nome: label || addr, ci, classe, bytes: 0 }
      v.bytes += c.bytes
      per.set(k, v)
    }
  }
  const righe: RigaBarra[] = [...per.entries()].sort((a, b) => b[1].bytes - a[1].bytes).slice(0, sinottico ? 10 : 8)
    .map(([k, v]) => ({ key: k, nome: v.ci ? <Link to={`/cis/${v.ci}`}>{v.nome}</Link> : v.nome, sotto: v.classe ?? undefined, valore: v.bytes, testo: formatBytes(v.bytes) }))
  return (
    <Widget titolo={t('Chi fa più traffico')} sotto={FINESTRA.valori.find((x) => x.value === w)?.label()} caricando={s.loading} errore={s.error} link={<Link to="/flows">{t('apri')}</Link>}>
      <ElencoBarre righe={righe} vuoto={t('Nessun traffico osservato in questa finestra.')} />
    </Widget>
  )
}

function TopConversazioni({ options }: { options: Record<string, unknown> }) {
  const w = finestraDi(options)
  const s = usePolling(() => api.conversations({ window: w, limit: 8 }), ogniMinuto, [w])
  const righe: RigaBarra[] = (s.data ?? []).map((c: Conversation, i) => ({
    key: `${c.src_addr}|${c.dst_addr}|${c.port}|${i}`, nome: `${c.src_label} → ${c.dst_label}`,
    sotto: c.service ?? `${c.port}/${c.protocol}`, valore: c.bytes, testo: formatBytes(c.bytes),
  }))
  return (
    <Widget titolo={t('Conversazioni principali')} sotto={FINESTRA.valori.find((x) => x.value === w)?.label()} caricando={s.loading} errore={s.error} link={<Link to="/flows">{t('apri')}</Link>}>
      <ElencoBarre righe={righe} vuoto={t('Nessun traffico osservato in questa finestra.')} />
    </Widget>
  )
}

function TopServizi({ options, by }: { options: Record<string, unknown>; by: 'service' | 'protocol' }) {
  const w = finestraDi(options)
  const s = usePolling(() => api.flowBreakdown({ by, window: w }), ogniMinuto, [w, by])
  const righe: RigaBarra[] = (s.data ?? []).slice(0, 8).map((r) => ({ key: String(r.key ?? r.label), nome: r.label, valore: r.bytes, testo: formatBytes(r.bytes) }))
  return (
    <Widget titolo={by === 'service' ? t('Servizi più usati') : t('Protocolli')} sotto={FINESTRA.valori.find((x) => x.value === w)?.label()} caricando={s.loading} errore={s.error} link={<Link to="/flows">{t('apri')}</Link>}>
      <ElencoBarre righe={righe} vuoto={t('Nessun traffico osservato in questa finestra.')} />
    </Widget>
  )
}

function Esportatori() {
  const s = usePolling(() => api.flowExporters(), ogniMinuto)
  const d = s.data ?? []
  const silenziosi = d.filter((e) => e.silent).length
  return (
    <Widget titolo={t('Esportatori di flussi')} caricando={s.loading} errore={s.error} link={<Link to="/flows/exporters">{t('apri')}</Link>}>
      <NumeroGrande valore={d.length} etichetta={t('apparati che mandano flussi')} tono={silenziosi > 0 ? 'warn' : d.length > 0 ? 'ok' : ''}
                    sotto={silenziosi > 0 ? t('{n} silenziosi', { n: silenziosi }) : undefined} />
    </Widget>
  )
}

function EntryPoints({ options }: { options: Record<string, unknown> }) {
  const w = finestraDi(options)
  const s = usePolling(() => api.entryPoints({ window: w, limit: 500 }), ogniMinuto, [w])
  const d = s.data ?? []
  return (
    <Widget titolo={t('Servizi esposti')} sotto={FINESTRA.valori.find((x) => x.value === w)?.label()} caricando={s.loading} errore={s.error} link={<Link to="/flows/entry-points">{t('apri')}</Link>}>
      <NumeroGrande valore={d.length} etichetta={t('indirizzi e porte che ricevono connessioni')} />
    </Widget>
  )
}

// ----------------------------------------------------------------- sicurezza

function Rischio() {
  const s = usePolling(() => api.securityRisk(), ogniMinuto * 5)
  const sintesi = s.data?.summary ?? {}
  const fasce: [string, string, string][] = [
    ['critico', maiuscola(t('critico')), 'var(--error)'], ['alto', maiuscola(t('alto')), 'var(--warn)'],
    ['medio', maiuscola(t('medio')), 'var(--info)'], ['basso', maiuscola(t('basso')), 'var(--text-muted)'],
    ['nessun rilievo', maiuscola(t('nessun rilievo')), 'var(--ok)'],
  ]
  const totale = Object.values(sintesi).reduce((a, b) => a + b, 0)
  return (
    <Widget titolo={t('Rischio degli asset')} caricando={s.loading} errore={s.error} link={<Link to="/security-health">{t('apri')}</Link>}>
      {s.data && (totale === 0 ? <p className="muted" style={{ margin: 0 }}>{t('Nessuna valutazione di sicurezza ancora eseguita.')}</p>
        : <Donut centreLabel={t('Asset')} slices={fasce.map(([key, label, color]) => ({ key, label, value: sintesi[key] ?? 0, color }))} />)}
    </Widget>
  )
}

function DobermannWidget() {
  const s = usePolling(() => api.dobermann(), ogniMinuto)
  const d = s.data
  const conta = d?.counts ?? {}
  const nuovi = conta['nuovo'] ?? 0
  return (
    <Widget titolo="Dobermann" caricando={s.loading} errore={s.error} link={<Link to="/dobermann">{t('apri')}</Link>}>
      {d && (d.watches.length === 0
        ? <p className="muted" style={{ margin: 0 }}>{t('Il Dobermann non è di guardia: dichiara le subnet da proteggere nella sua pagina.')}</p>
        : (nuovi + (conta['ignorato'] ?? 0) + (conta['censito'] ?? 0)) === 0
          ? <p className="muted" style={{ margin: 0 }}>{t('Nessun avvistamento: tutto ciò che parla nelle {n} subnet sorvegliate è censito.', { n: d.watches.length })}</p>
          : (
            <>
              <Donut centreLabel={t('Avvistamenti')} slices={[
                { key: 'nuovo', label: maiuscola(t('nuovo')), value: nuovi, color: 'var(--error)' },
                { key: 'ignorato', label: maiuscola(t('ignorato')), value: conta['ignorato'] ?? 0, color: 'var(--text-muted)' },
                { key: 'censito', label: maiuscola(t('censito')), value: conta['censito'] ?? 0, color: 'var(--ok)' },
              ]} />
              {nuovi > 0 && <p className="eagle-motivo" style={{ margin: '6px 0 0' }}>{nuovi === 1 ? t('1 indirizzo mai censito sta facendo traffico.') : t('{n} indirizzi mai censiti stanno facendo traffico.', { n: nuovi })}</p>}
            </>
          ))}
    </Widget>
  )
}

function DobermannTalkers({ options }: { options: Record<string, unknown> }) {
  const g = giorniDi(options)
  const s = usePolling(() => api.dobermannStats(g), ogniMinuto * 5, [g])
  const righe: RigaBarra[] = (s.data?.top_talkers ?? []).slice(0, 8).map((x) => ({
    key: x.id, nome: <Link to="/dobermann">{x.address}</Link>, sotto: `${x.watch_cidr} · ${x.stato}`,
    valore: x.bytes_total, testo: formatBytes(x.bytes_total), tono: x.stato === 'nuovo' ? 'error' : undefined,
  }))
  return (
    <Widget titolo={t('Sconosciuti che parlano di più')} sotto={t('ultimi {n} giorni', { n: g })} caricando={s.loading} errore={s.error} link={<Link to="/dobermann">{t('apri')}</Link>}>
      <ElencoBarre righe={righe} vuoto={t('Nessun avvistamento nel periodo.')} />
    </Widget>
  )
}

function HoneypotWidget({ options }: { options: Record<string, unknown> }) {
  const g = giorniDi(options)
  const s = usePolling(() => api.honeypotStats(g), ogniMinuto * 5, [g])
  const d = s.data
  return (
    <Widget titolo="Honeypot" sotto={t('ultimi {n} giorni', { n: g })} caricando={s.loading} errore={s.error} link={<Link to="/honeypot">{t('apri')}</Link>}>
      {d && (
        <>
          <NumeroGrande valore={d.summary.hits_window} tono={d.summary.hits_window > 0 ? 'error' : 'ok'} etichetta={t('tocchi sulle esche')}
                        sotto={t('{a} sorgenti · {b} con credenziali', { a: d.summary.sources, b: d.summary.with_credentials })} />
          {d.top_sources.length > 0 && <div style={{ marginTop: 8 }}>
            <ElencoBarre righe={d.top_sources.slice(0, 5).map((x) => ({ key: x.address, nome: x.address, valore: x.hits, testo: String(x.hits), tono: 'error' as const }))} vuoto="" />
          </div>}
        </>
      )}
    </Widget>
  )
}

function WazuhWidget() {
  const s = usePolling(() => api.wazuhOverview(), ogniMinuto * 5)
  const d = s.data
  const a = d?.agents
  const agenti: StatusSlice[] = [
    { key: 'active', label: t('attivo'), value: a?.active ?? 0, tone: 'ok' },
    { key: 'disconnected', label: t('disconnesso'), value: a?.disconnected ?? 0, tone: 'warn' },
    { key: 'pending', label: t('in attesa'), value: a?.pending ?? 0, tone: 'info' },
    { key: 'never', label: t('mai connesso'), value: a?.never_connected ?? 0, tone: 'error' },
  ]
  const coloreGravita: Record<string, string> = { critical: 'var(--error)', high: 'var(--warn)', medium: 'var(--info)', low: 'var(--text-muted)' }
  const gravita = d?.vulnerabilities?.by_severity ?? []
  return (
    <Widget titolo="Wazuh" caricando={s.loading} errore={s.error} link={<Link to="/wazuh">{t('apri')}</Link>}>
      {d && (!d.available ? <p className="muted" style={{ margin: 0 }}>{d.reason || t('Wazuh non è interrogabile.')}</p> : (
        <>
          <StatusStrip data={agenti} emptyHint={t('Nessun agente registrato.')} />
          {d.indexer_available && gravita.length > 0 && <div style={{ marginTop: 10 }}>
            <Donut centreLabel={t('vulnerabilità')} slices={gravita.map((b) => ({ key: b.severity || '—', label: b.severity || '—', value: b.count ?? 0, color: coloreGravita[(b.severity || '').trim().toLowerCase()] ?? 'var(--muted-bg)' }))} />
          </div>}
        </>
      ))}
    </Widget>
  )
}

function EndOfSupportWidget() {
  const s = usePolling(() => api.endOfSupport(), ogniMinuto * 10)
  const n = s.data?.products?.length ?? 0
  return (
    <Widget titolo={t('Fuori supporto')} caricando={s.loading} errore={s.error} link={<Link to="/security-health">{t('apri')}</Link>}>
      {s.data && <NumeroGrande valore={n} tono={n > 0 ? 'warn' : 'ok'} etichetta={t('prodotti a fine supporto in inventario')} />}
    </Widget>
  )
}

// ---------------------------------------------------------------- protezione

const TIPI_APPARATO = new Set(['network_device', 'firewall', 'router', 'switch', 'load_balancer'])
const BACKUP_FERMO_DOPO_GIORNI = 7

function BackupApparati() {
  const s = useSummary()
  const disp = usePolling(() => api.configDevices(), ogniMinuto * 5)
  const salvati = disp.data?.devices ?? []
  const adesso = Date.now()
  let recenti = 0
  let fermi = 0
  for (const d of salvati) {
    const quando = d.last_change ? Date.parse(d.last_change) : NaN
    if (!Number.isNaN(quando) && adesso - quando <= BACKUP_FERMO_DOPO_GIORNI * 86_400_000) recenti += 1
    else fermi += 1
  }
  const apparati = Object.entries(s.data?.cis.by_type ?? {}).filter(([tipo]) => TIPI_APPARATO.has(tipo)).reduce((sum, [, n]) => sum + n, 0)
  const senza = Math.max(0, apparati - salvati.length)
  return (
    <Widget titolo={t('Backup delle configurazioni')} caricando={disp.loading || s.loading} errore={disp.error} link={<Link to="/device-configs">{t('apri')}</Link>}>
      {disp.data && s.data && (apparati === 0 && salvati.length === 0
        ? <p className="muted" style={{ margin: 0 }}>{t('Nessun apparato di rete in CMDB.')}</p>
        : <Donut centreLabel={t('Apparati')} slices={[
            { key: 'recenti', label: t('Backup recente (≤{giorni} gg)', { giorni: BACKUP_FERMO_DOPO_GIORNI }), value: recenti, color: 'var(--ok)' },
            { key: 'fermi', label: t('Backup fermo'), value: fermi, color: 'var(--warn)' },
            { key: 'senza', label: t('Senza backup'), value: senza, color: 'var(--error)' },
          ]} />)}
    </Widget>
  )
}

function BackupVm({ sinottico }: { sinottico: boolean }) {
  const s = usePolling(() => api.backupVmProtection(), ogniMinuto * 5)
  const [fetta, setFetta] = useState<'protected' | 'unprotected' | null>(null)
  const d = s.data
  const conteggi = d?.counts ?? { protected: 0, unprotected: 0 }
  const elenco: VeeamVmEntry[] = fetta && d ? d[fetta] ?? [] : []
  const nomeVm = (r: VeeamVmEntry) => r.name || t('(senza nome)')
  return (
    <Widget titolo={t('VM protette da backup')} caricando={s.loading} errore={s.error} link={<Link to="/backup">{t('apri')}</Link>}>
      {d && (!d.available ? <p className="muted" style={{ margin: 0 }}>{d.reason}</p>
        : !d.total ? <p className="muted" style={{ margin: 0 }}>{t('Nessuna macchina virtuale in CMDB.')}</p>
        : (
          <>
            <Donut centreLabel={t('VM')} selected={fetta} onSliceClick={sinottico ? undefined : (k) => setFetta(fetta === k ? null : (k as 'protected' | 'unprotected'))}
                   slices={[
                     { key: 'protected', label: t('Protette'), value: conteggi.protected, color: 'var(--ok)' },
                     { key: 'unprotected', label: t('Senza backup'), value: conteggi.unprotected, color: 'var(--error)' },
                   ]} />
            {fetta && (
              <DataTable rows={elenco} rowKey={(r) => r.ci_id} initialSort={{ key: 'name' }} emptyText={t('Nessuna VM in questo gruppo.')}
                columns={fetta === 'protected'
                  ? [{ key: 'name', header: t('VM'), value: nomeVm, render: (r) => <Link to={`/cis/${r.ci_id}`}>{nomeVm(r)}</Link> },
                     { key: 'object', header: t('Oggetto di backup'), value: (r) => r.matched.map((m) => m.name ?? '').join(', ') }]
                  : [{ key: 'name', header: t('VM senza backup'), value: nomeVm, render: (r) => <Link to={`/cis/${r.ci_id}`}>{nomeVm(r)}</Link> }]} />
            )}
          </>
        ))}
    </Widget>
  )
}

function AssetWidget() {
  const s = usePolling(() => api.assetSummary(), ogniMinuto * 5)
  const d = s.data
  return (
    <Widget titolo={t('Asset e contratti')} caricando={s.loading} errore={s.error} link={<Link to="/assets">{t('apri')}</Link>}>
      {d && (
        <>
          <Donut centreLabel={t('Asset')} slices={[
            { key: 'coperti', label: t('Coperti da contratto'), value: d.covered, color: 'var(--ok)' },
            { key: 'scoperti', label: t('Senza copertura'), value: d.uncovered, color: 'var(--warn)' },
          ]} />
          {d.cis_not_promoted > 0 && <p className="muted eagle-piccolo" style={{ margin: '6px 0 0' }}>{t('{quanti} CI osservati non sono ancora registrati come beni.', { quanti: d.cis_not_promoted })}</p>}
        </>
      )}
    </Widget>
  )
}

// ------------------------------------------------------------ capacità e costi

function CapacitaStorage() {
  const s = usePolling(() => api.capacity(), ogniMinuto * 10)
  const tot = s.data?.storage.total
  const pct = tot?.used_percent ?? null
  return (
    <Widget titolo={t('Storage')} caricando={s.loading} errore={s.error} link={<Link to="/capacity">{t('apri')}</Link>}>
      {tot && <NumeroGrande valore={pct == null ? '—' : `${Math.round(pct)} %`} etichetta={t('usato')}
                            tono={pct == null ? '' : pct >= 90 ? 'error' : pct >= 80 ? 'warn' : 'ok'}
                            sotto={t('{libero} liberi su {tot}', { libero: formatBytes(tot.free_bytes), tot: formatBytes(tot.capacity_bytes) })} />}
    </Widget>
  )
}

function CapacitaCompute() {
  const s = usePolling(() => api.capacity(), ogniMinuto * 10)
  const c = s.data?.compute.total
  return (
    <Widget titolo={t('Compute')} caricando={s.loading} errore={s.error} link={<Link to="/capacity">{t('apri')}</Link>}>
      {c && <NumeroGrande valore={c.vms ?? 0} etichetta={t('VM su {n} host', { n: c.hosts })}
                          sotto={c.cpu_overcommit != null ? t('CPU allocata {x}× · memoria {y}×', { x: c.cpu_overcommit.toFixed(1), y: (c.memory_overcommit ?? 0).toFixed(1) }) : undefined} />}
    </Widget>
  )
}

function CostiMese() {
  const s = usePolling(() => api.costCenter(), ogniMinuto * 30)
  const d = s.data
  const prec = d?.previous_month_same_day ?? 0
  const delta = d && prec > 0 ? ((d.month_to_date - prec) / prec) * 100 : null
  return (
    <Widget titolo={t('Costi cloud del mese')} caricando={s.loading} errore={s.error} link={<Link to="/cost-center">{t('apri')}</Link>}>
      {d && <NumeroGrande valore={`${d.month_to_date.toLocaleString(localeCorrente(), { maximumFractionDigits: 0 })} ${d.currency}`}
                          etichetta={t('dall’inizio del mese')}
                          delta={delta == null ? null : { testo: t('{d}% rispetto allo stesso giorno del mese scorso', { d: (delta >= 0 ? '+' : '') + delta.toFixed(0) }), peggio: delta > 0 }}
                          sotto={t('stima a fine mese {v}', { v: `${d.month_end_estimate.toLocaleString(localeCorrente(), { maximumFractionDigits: 0 })} ${d.currency}` })} />}
    </Widget>
  )
}

function CostiServizi() {
  const s = usePolling(() => api.costCenter(), ogniMinuto * 30)
  const d = s.data
  const righe: RigaBarra[] = (d?.by_service ?? []).slice(0, 8).map((r) => ({ key: r.key, nome: r.key, valore: r.amount, testo: `${r.amount.toLocaleString(localeCorrente(), { maximumFractionDigits: 0 })} ${d?.currency ?? ''}` }))
  return (
    <Widget titolo={t('Costi per servizio')} caricando={s.loading} errore={s.error} link={<Link to="/cost-center">{t('apri')}</Link>}>
      <ElencoBarre righe={righe} vuoto={t('Nessun costo nel periodo.')} />
    </Widget>
  )
}

// --------------------------------------------------------------------- Eagle

/**
 * Una sola richiesta per la stessa panoramica: quattro widget la chiedono
 * nello stesso istante all'apertura della pagina (e a ogni giro del minuto),
 * e quattro richieste da secondi in parallelo erano quattro volte l'attesa.
 * Chi arriva mentre una è in corso riceve la stessa promessa.
 */
const panoramicheInCorso = new Map<number, Promise<EagleOverview>>()
function panoramicaCondivisa(ore: number): Promise<EagleOverview> {
  const inCorso = panoramicheInCorso.get(ore)
  if (inCorso) return inCorso
  const p = api.eagleOverview(ore).finally(() => panoramicheInCorso.delete(ore))
  panoramicheInCorso.set(ore, p)
  return p
}

function useEagleOverview(o: Record<string, unknown>) {
  const ore = Number(o.hours ?? 24)
  return { ore, s: usePolling(() => panoramicaCondivisa(ore), ogniMinuto, [ore]) }
}

function EagleProblemi({ options }: { options: Record<string, unknown> }) {
  const { s } = useEagleOverview(options)
  const d = s.data
  const gravi = d?.licensed ? d.totals.by_status.crit + d.totals.by_status.down : 0
  return (
    <Widget titolo={t('Eagle · problemi gravi')} caricando={s.loading} errore={s.error} link={<Link to="/eagle">{t('apri')}</Link>}>
      {d?.licensed && <NumeroGrande valore={gravi} tono={gravi > 0 ? 'error' : d.totals.by_status.warn > 0 ? 'warn' : 'ok'}
                                    etichetta={t('critici o che non rispondono')}
                                    sotto={<><span className="numero-grande__segmenti"><Segmenti parti={segmentiDaConteggi(d.totals.by_status)} altezza={5} /></span>{t('{n} in attenzione · {m} CI monitorati', { n: d.totals.by_status.warn, m: d.totals.cis })}</>} />}
    </Widget>
  )
}

function EagleAllarmi({ options }: { options: Record<string, unknown> }) {
  const { s, ore } = useEagleOverview(options)
  const d = s.data
  return (
    <Widget titolo={t('Eagle · parametri in allarme nel tempo')} caricando={s.loading} errore={s.error} link={<Link to="/eagle">{t('apri')}</Link>}>
      {d?.licensed && <GraficoStati punti={d.timeline} passoS={d.step_s} ore={ore} altezza={176} />}
    </Widget>
  )
}

function EagleElenco({ options, sinottico }: { options: Record<string, unknown>; sinottico: boolean }) {
  const { s } = useEagleOverview(options)
  const d = s.data
  const righe = d?.licensed ? d.problems.slice(0, sinottico ? 12 : 8) : []
  // A click opens the parameter in a window, without leaving the dashboard.
  const [aperto, setAperto] = useState<ParametroDaAprire | null>(null)
  return (
    <Widget titolo={t('Eagle · problemi aperti')} caricando={s.loading} errore={s.error} link={<Link to="/eagle">{t('apri')}</Link>}>
      {aperto && <FinestraParametro scelto={aperto} onChiudi={() => setAperto(null)} />}
      {d?.licensed && (righe.length === 0 ? <p className="muted" style={{ margin: 0 }}>{t('Nessun parametro in attenzione, critico o che non risponde.')}</p> : (
        <div className="esin__problemi">
          {righe.map((p) => (
            <div key={`${p.ci_id}|${p.key}`} className={`esin__problema esin__problema--${tonoStato(p.status)}`}>
              <div>
                <button type="button" className="linklike" title={t('Apri il grafico del parametro')}
                        onClick={() => setAperto({ ci_id: p.ci_id, ci_name: p.ci_name, key: p.key, name: p.name, kind: p.kind, unit: p.unit })}>
                  <strong>{p.ci_name}</strong>
                </button>
                {' '}<span className="muted">· {etichettaParametro(p)}</span>
              </div>
              <div className="esin__problema-dettaglio">
                {p.value != null && <span className="mono">{formatConUnita(p.value, p.unit)} </span>}
                <span className="muted">{p.error ?? etichettaStato(p.status)} · {timeAgo(p.since)}{p.probe ? ` · ${p.probe}` : ''}</span>
                {p.cause && <> <Causa c={p.cause} /></>}
              </div>
            </div>
          ))}
        </div>
      ))}
    </Widget>
  )
}

function EagleStati({ options }: { options: Record<string, unknown> }) {
  const { s } = useEagleOverview(options)
  const d = s.data
  const b = d?.licensed ? d.totals.by_status : null
  return (
    <Widget titolo={t('Eagle · parametri per stato')} sotto={d?.licensed ? t('{n} parametri su {m} CI', { n: d.totals.params, m: d.totals.cis }) : undefined}
            caricando={s.loading} errore={s.error} link={<Link to="/eagle">{t('apri')}</Link>}>
      {b && (b.ok + b.warn + b.crit + b.down + b.pending + b.nodata === 0
        ? <p className="muted" style={{ margin: 0 }}>{t('Nessun CI monitorato.')}</p>
        : <Donut centreLabel={t('parametri')} slices={[
            { key: 'ok', label: t('ok'), value: b.ok, color: 'var(--ok)' },
            { key: 'warn', label: t('attenzione'), value: b.warn, color: 'var(--warn)' },
            { key: 'gravi', label: t('critici o che non rispondono'), value: b.crit + b.down, color: 'var(--error)' },
            { key: 'pending', label: t('in attesa'), value: b.pending + b.nodata, color: 'var(--text-muted)' },
          ]} />)}
    </Widget>
  )
}

// ------------------------------------------------------------------ catalogo

export const CATALOGO: DefinizioneWidget[] = [
  { tipo: 'ci-gestiti', gruppo: 'panoramica', size: 's', label: () => t('CI gestiti'), descrizione: () => t('Quanti CI sono riconosciuti da almeno una fonte.'), Componente: () => <CIGestiti /> },
  { tipo: 'host-non-gestiti', gruppo: 'panoramica', size: 's', label: () => t('Host non gestiti'), descrizione: () => t('Visti in rete senza accesso: la coda di lavoro.'), Componente: () => <HostNonGestiti /> },
  { tipo: 'sonde-online', gruppo: 'panoramica', size: 's', label: () => t('Sonde in linea'), descrizione: () => t('Le sonde che rispondono, sul totale.'), modulo: 'fleet', Componente: () => <SondeOnline /> },
  { tipo: 'job-falliti', gruppo: 'panoramica', size: 's', label: () => t('Job falliti'), descrizione: () => t('I job di discovery terminati con errore.'), modulo: 'jobs', Componente: () => <JobFalliti /> },
  { tipo: 'ci-tipo', gruppo: 'panoramica', size: 'l', label: () => t('CI per tipo'), descrizione: () => t('La composizione del parco.'), Componente: () => <CIPerTipo /> },
  { tipo: 'movimenti', gruppo: 'panoramica', size: 'l', label: () => t('Movimenti dell’inventario'), descrizione: () => t('Nuovi, non più visti, da classificare negli ultimi 7 giorni.'), Componente: () => <Movimenti /> },
  { tipo: 'job-stati', gruppo: 'panoramica', size: 'm', label: () => t('Job per stato'), descrizione: () => t('Riusciti, in corso, in attesa, falliti.'), modulo: 'jobs', Componente: () => <JobStati /> },
  { tipo: 'sonde-stati', gruppo: 'panoramica', size: 'm', label: () => t('Sonde per stato'), descrizione: () => t('In linea, degradate, non raggiungibili.'), modulo: 'fleet', Componente: () => <SondeStati /> },
  { tipo: 'scansioni', gruppo: 'panoramica', size: 'xl', tall: true, label: () => t('Scansioni'), descrizione: () => t('Quanto si scopre nel tempo: nuovi e scomparsi.'), modulo: 'jobs', opzioni: [GIORNI], Componente: (p) => <Scansioni {...p} /> },
  { tipo: 'reperti', gruppo: 'panoramica', size: 'm', label: () => t('Rischi sui CI'), descrizione: () => t('I rischi per gravità.'), modulo: 'security_health', Componente: () => <Reperti /> },

  { tipo: 'traffico-volume', gruppo: 'traffico', size: 'xl', tall: true, label: () => t('Volume di traffico'), descrizione: () => t('L’andamento del traffico nella finestra.'), modulo: 'flows', opzioni: [FINESTRA], Componente: (p) => <TrafficoVolume {...p} /> },
  { tipo: 'top-talkers', gruppo: 'traffico', size: 'l', tall: true, label: () => t('Chi fa più traffico'), descrizione: () => t('Gli indirizzi e i CI con più byte, in entrata e in uscita.'), modulo: 'flows', opzioni: [FINESTRA], Componente: (p) => <TopTalkers {...p} /> },
  { tipo: 'top-conversazioni', gruppo: 'traffico', size: 'l', tall: true, label: () => t('Conversazioni principali'), descrizione: () => t('Le coppie che si parlano di più.'), modulo: 'flows', opzioni: [FINESTRA], Componente: (p) => <TopConversazioni {...p} /> },
  { tipo: 'top-servizi', gruppo: 'traffico', size: 'm', tall: true, label: () => t('Servizi più usati'), descrizione: () => t('Il traffico per servizio (porta).'), modulo: 'flows', opzioni: [FINESTRA], Componente: (p) => <TopServizi {...p} by="service" /> },
  { tipo: 'top-protocolli', gruppo: 'traffico', size: 'm', label: () => t('Protocolli'), descrizione: () => t('Il traffico per protocollo.'), modulo: 'flows', opzioni: [FINESTRA], Componente: (p) => <TopServizi {...p} by="protocol" /> },
  { tipo: 'esportatori', gruppo: 'traffico', size: 's', label: () => t('Esportatori di flussi'), descrizione: () => t('Gli apparati che mandano flussi, e quelli silenziosi.'), modulo: 'exporters', Componente: () => <Esportatori /> },
  { tipo: 'entry-points', gruppo: 'traffico', size: 's', label: () => t('Servizi esposti'), descrizione: () => t('Indirizzi e porte che ricevono connessioni.'), modulo: 'entry_points', opzioni: [FINESTRA], Componente: (p) => <EntryPoints {...p} /> },

  { tipo: 'rischio', gruppo: 'sicurezza', size: 'm', label: () => t('Rischio degli asset'), descrizione: () => t('Gli asset per fascia di rischio.'), modulo: 'security_health', Componente: () => <Rischio /> },
  { tipo: 'dobermann', gruppo: 'sicurezza', size: 'm', label: () => 'Dobermann', descrizione: () => t('Gli avvistamenti di chi parla senza essere censito.'), modulo: 'dobermann', Componente: () => <DobermannWidget /> },
  { tipo: 'dobermann-talkers', gruppo: 'sicurezza', size: 'l', tall: true, label: () => t('Sconosciuti che parlano di più'), descrizione: () => t('Gli indirizzi non censiti con più traffico.'), modulo: 'dobermann', opzioni: [GIORNI], Componente: (p) => <DobermannTalkers {...p} /> },
  { tipo: 'honeypot', gruppo: 'sicurezza', size: 'm', label: () => 'Honeypot', descrizione: () => t('I tocchi sulle esche e chi li fa.'), modulo: 'honeypot', opzioni: [GIORNI], Componente: (p) => <HoneypotWidget {...p} /> },
  { tipo: 'wazuh', gruppo: 'sicurezza', size: 'm', label: () => 'Wazuh', descrizione: () => t('Gli agenti per stato e le vulnerabilità.'), modulo: 'wazuh', Componente: () => <WazuhWidget /> },
  { tipo: 'end-of-support', gruppo: 'sicurezza', size: 's', label: () => t('Fuori supporto'), descrizione: () => t('I prodotti a fine supporto in inventario.'), modulo: 'security_health', Componente: () => <EndOfSupportWidget /> },

  { tipo: 'backup', gruppo: 'protezione', size: 'm', label: () => t('Backup delle configurazioni'), descrizione: () => t('Apparati con backup recente, fermo, o senza.'), modulo: 'device_configs', Componente: () => <BackupApparati /> },
  { tipo: 'backup-vm', gruppo: 'protezione', size: 'm', label: () => t('VM protette da backup'), descrizione: () => t('Le VM con e senza backup.'), modulo: 'backup', Componente: (p) => <BackupVm {...p} /> },
  { tipo: 'asset', gruppo: 'protezione', size: 'm', label: () => t('Asset e contratti'), descrizione: () => t('Coperti da contratto e scoperti.'), modulo: 'assets', Componente: () => <AssetWidget /> },

  { tipo: 'capacita-storage', gruppo: 'capacita', size: 's', label: () => t('Storage'), descrizione: () => t('Quanto storage è usato e quanto ne resta.'), modulo: 'capacity', Componente: () => <CapacitaStorage /> },
  { tipo: 'capacita-compute', gruppo: 'capacita', size: 's', label: () => t('Compute'), descrizione: () => t('VM, host e allocazione.'), modulo: 'capacity', Componente: () => <CapacitaCompute /> },
  { tipo: 'costi-mese', gruppo: 'capacita', size: 'm', label: () => t('Costi cloud del mese'), descrizione: () => t('Speso finora, confronto e stima a fine mese.'), modulo: 'cost_center', Componente: () => <CostiMese /> },
  { tipo: 'costi-servizi', gruppo: 'capacita', size: 'm', tall: true, label: () => t('Costi per servizio'), descrizione: () => t('Dove vanno i soldi.'), modulo: 'cost_center', Componente: () => <CostiServizi /> },

  { tipo: 'eagle-problemi', gruppo: 'eagle', size: 's', eagle: true, label: () => t('Eagle · problemi gravi'), descrizione: () => t('Il numero che conta: critici e non risponde.'), opzioni: [ORE_EAGLE], Componente: (p) => <EagleProblemi {...p} /> },
  { tipo: 'eagle-stati', gruppo: 'eagle', size: 'm', tall: true, eagle: true, label: () => t('Eagle · parametri per stato'), descrizione: () => t('Ok, attenzione, critico, non risponde.'), opzioni: [ORE_EAGLE], Componente: (p) => <EagleStati {...p} /> },
  { tipo: 'eagle-allarmi', gruppo: 'eagle', size: 'l', tall: true, eagle: true, label: () => t('Eagle · parametri in allarme nel tempo'), descrizione: () => t('L’andamento degli allarmi.'), opzioni: [ORE_EAGLE], Componente: (p) => <EagleAllarmi {...p} /> },
  { tipo: 'eagle-elenco', gruppo: 'eagle', size: 'l', tall: true, eagle: true, label: () => t('Eagle · problemi aperti'), descrizione: () => t('I parametri in attenzione, critici o giù.'), opzioni: [ORE_EAGLE], Componente: (p) => <EagleElenco {...p} /> },
]

// I widget di composizione (generali.tsx): un'immagine e un testo, per il
// sinottico a matrice libera. In ogni dashboard, Eagle compreso.
const IMMAGINE: OpzioneWidget = { key: 'src', predefinito: '', valori: [], dinamica: 'immagine', label: () => t('Immagine') }
const ADATTA: OpzioneWidget = {
  key: 'fit', predefinito: 'contain', label: () => t('Adatta'),
  valori: [
    { value: 'contain', label: () => t('tutta dentro') }, { value: 'cover', label: () => t('riempi, tagliando') },
    { value: 'fill', label: () => t('stira') },
  ],
}
const DIDASCALIA: OpzioneWidget = { key: 'caption', predefinito: '', tipo: 'testo', valori: [], label: () => t('Didascalia (facoltativa)') }
const CORNICE: OpzioneWidget = { key: 'cornice', predefinito: 'false', tipo: 'bool', valori: [], label: () => t('Con cornice e titolo') }
const TESTO: OpzioneWidget = { key: 'text', predefinito: '', tipo: 'multilinea', valori: [], label: () => t('Testo') }
const DIMENSIONE_TESTO: OpzioneWidget = {
  key: 'size', predefinito: 'm', label: () => t('Dimensione'),
  valori: [
    { value: 's', label: () => t('piccolo') }, { value: 'm', label: () => t('normale') },
    { value: 'l', label: () => t('grande') }, { value: 'xl', label: () => t('titolo') },
  ],
}
const ALLINEA: OpzioneWidget = {
  key: 'align', predefinito: 'left', label: () => t('Allineamento'),
  valori: [
    { value: 'left', label: () => t('a sinistra') }, { value: 'center', label: () => t('al centro') },
    { value: 'right', label: () => t('a destra') },
  ],
}
CATALOGO.push(
  { tipo: 'immagine', gruppo: 'generale', size: 'l', tall: true, label: () => t('Immagine'), descrizione: () => t('Una planimetria, uno schema, un logo: un file dal computer o un indirizzo.'), opzioni: [IMMAGINE, ADATTA, DIDASCALIA, CORNICE], Componente: (p) => <Immagine {...p} /> },
  { tipo: 'testo', gruppo: 'generale', size: 'm', label: () => t('Testo'), descrizione: () => t('Un titolo, una didascalia, una legenda.'), opzioni: [TESTO, DIMENSIONE_TESTO, ALLINEA, CORNICE], Componente: (p) => <Testo {...p} /> },
)

// I widget del cruscotto di Eagle (catalogoEagle.tsx), nello stesso catalogo.
for (const w of CATALOGO_EAGLE) {
  CATALOGO.push({ tipo: w.tipo, gruppo: 'eagle', eagle: true, size: w.size, tall: w.tall, label: w.label,
                  descrizione: w.descrizione, opzioni: w.opzioni, Componente: (p) => w.Componente(p) })
}

export const PER_TIPO: Record<string, DefinizioneWidget> = Object.fromEntries(CATALOGO.map((w) => [w.tipo, w]))

/** Disegna un widget del catalogo; un tipo che non esiste più lo dice. */
export function DisegnaWidget({ tipo, options, sinottico }: { tipo: string; options: Record<string, unknown>; sinottico: boolean }) {
  const def = PER_TIPO[tipo]
  if (!def) return <Widget titolo={tipo}><p className="muted" style={{ margin: 0 }}>{t('Questo widget non esiste più: toglilo dalla dashboard.')}</p></Widget>
  // Il titolo scelto dalla persona (opzione `title`, uguale per ogni widget).
  const titolo = typeof options.title === 'string' ? options.title : undefined
  return <TitoloWidgetContext.Provider value={titolo}>{def.Componente({ options, sinottico })}</TitoloWidgetContext.Provider>
}
